import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash';
import Button from 'reactstrap/lib/Button';
import { Link } from 'site-modules/shared/components/link/link';
import {
  BUTTON_DATA,
  ERROR_SCREEN_CREATIVE_ID,
  INPUT_DELAY,
  PRIMING_BOX_COPY,
} from 'site-modules/shared/constants/global-search/global-search';
import { GlobalSearchLinks } from 'site-modules/shared/components/inventory/global-search/global-search-links/global-search-links';
import { GlobalSearchLoadingSeparator } from 'site-modules/shared/components/inventory/global-search/global-search-loading-separator/global-search-loading-separator';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';
import { fireWidgetViewTracking } from 'site-modules/shared/utils/inventory/global-search-tracking';
import { useDialogFocusTrap } from 'site-modules/shared/hooks/global-search/use-dialog-focus-trap';

import './global-search-results.scss';

export const GlobalSearchResults = memo(
  ({
    searchQuery,
    isMobile,
    isError,
    isOpen,
    fastMatcherData,
    isFastMatcherLoading,
    searchId,
    creativeId,
    withLlmSearchDisabled,
    onLinkClick,
  }) => {
    const trimmedQuery = searchQuery.trim();
    const [isResultsLoading, setIsResultsLoading] = useState(isFastMatcherLoading);
    const debouncedIsLoading = useDebounce(setIsResultsLoading, INPUT_DELAY);
    const searchResultsRef = useRef(null);
    const isLlmDisabledError = withLlmSearchDisabled && trimmedQuery && !fastMatcherData?.delta && !isResultsLoading;

    useDialogFocusTrap(searchResultsRef);

    useEffect(() => {
      if (isFastMatcherLoading) {
        setIsResultsLoading(isFastMatcherLoading);
      } else {
        debouncedIsLoading(isFastMatcherLoading);
      }

      return () => {
        if (!isFastMatcherLoading) {
          debouncedIsLoading.cancel();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFastMatcherLoading]);

    useEffect(() => {
      if (isMobile && !searchQuery) {
        fireWidgetViewTracking({ value: PRIMING_BOX_COPY, creativeId });
      }
    }, [creativeId, isMobile, searchQuery]);

    useEffect(() => {
      if (!isMobile && isOpen && !searchQuery) {
        fireWidgetViewTracking({ value: PRIMING_BOX_COPY, creativeId });
      }
    }, [creativeId, isMobile, isOpen, searchQuery]);

    useEffect(() => {
      if (isError || isLlmDisabledError) {
        fireWidgetViewTracking({ creativeId: ERROR_SCREEN_CREATIVE_ID, value: `srp_search_id=${searchId}` });
      }
    }, [isError, isLlmDisabledError, searchId]);

    const quickLinks = (
      <>
        <h2 className={classnames('medium fw-bold text-cool-gray-10 py-0_5 mb-0', { 'ps-1': !isMobile })}>
          Quick Links
        </h2>
        <ul
          className={classnames('quick-links list-unstyled mb-0 d-flex flex-column align-items-start', {
            'px-0_5': !isMobile,
          })}
        >
          {BUTTON_DATA.map(({ id, label, url, dataTrackingId }) => (
            <li className="w-100" key={id}>
              <Button
                key={id}
                tag={Link}
                color="outline"
                to={url}
                className={classnames(
                  'quick-link text-cool-gray-30 text-transform-none text-underline py-0_75 px-0 size-16',
                  {
                    'ps-0_5 w-100 text-start': !isMobile,
                  }
                )}
                data-tracking-id={dataTrackingId}
              >
                {label}
              </Button>
            </li>
          ))}
        </ul>
      </>
    );

    return (
      <div
        ref={searchResultsRef}
        className={classnames('global-search-results pos-r', {
          'pt-0_5 pt-md-1': trimmedQuery || !isMobile || !isError,
        })}
      >
        <GlobalSearchLoadingSeparator isMobile={isMobile} showAnimation={!!trimmedQuery && isResultsLoading} />

        {(isError || isLlmDisabledError) && (
          <>
            <div className="mb-1_5 mt-md-1 px-md-1">
              <h3 className="heading-5">Please try adjusting your search</h3>
              <p className="size-16">No results for &quot;{searchQuery}&quot;</p>
            </div>
            {quickLinks}
          </>
        )}

        {!trimmedQuery && !isError && (
          <>
            <div
              className={classnames({
                'px-0_5': !isMobile,
              })}
            >
              <p className="priming-box heading-5 fw-normal text-blue-10 fst-italic py-1 px-1 mb-1 rounded-16">
                {isMobile && <span className="icon-sparkles icon me-0_5 pt-0_25 medium" aria-hidden />}
                {PRIMING_BOX_COPY}
              </p>
            </div>

            {quickLinks}
          </>
        )}

        {trimmedQuery && !isError && !isLlmDisabledError && (
          <div aria-live="polite" aria-atomic>
            <GlobalSearchLinks
              query={trimmedQuery}
              fastMatcherData={fastMatcherData}
              creativeId={creativeId}
              isMobile={isMobile}
              onLinkClick={onLinkClick}
            />
          </div>
        )}
      </div>
    );
  }
);

GlobalSearchResults.displayName = 'GlobalSearchResults';

GlobalSearchResults.propTypes = {
  searchQuery: PropTypes.string,
  isMobile: PropTypes.bool,
  isError: PropTypes.bool,
  isOpen: PropTypes.bool,
  fastMatcherData: PropTypes.shape({
    delta: PropTypes.string,
    struct: PropTypes.shape({}),
  }),
  isFastMatcherLoading: PropTypes.bool,
  searchId: PropTypes.string,
  creativeId: PropTypes.string,
  onLinkClick: PropTypes.func,
  withLlmSearchDisabled: PropTypes.bool,
};

GlobalSearchResults.defaultProps = {
  searchQuery: '',
  isMobile: false,
  isError: false,
  isOpen: false,
  fastMatcherData: null,
  isFastMatcherLoading: false,
  searchId: '',
  creativeId: '',
  onLinkClick: noop,
  withLlmSearchDisabled: false,
};
